import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  MapPinIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  StarIcon,
  HeartIcon,
  EyeIcon,
  CameraIcon,
  SparklesIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';
import { useAppSelector } from '../../hooks/redux';
import { dashboardAPI } from '../../services/api';
import { NearbyOpportunity } from '../../types';
import LocationText from '../common/LocationText';

import Avatar from '../common/Avatar';

interface Recommendation {
  id: string;
  type: 'opportunity' | 'collaboration' | 'user';
  title: string;
  description: string;
  creator_id?: string;
  creator?: {
    name: string;
    avatar?: string;
    rating?: number;
    id: string;
  };
  location?: string;
  distance_km?: number;
  budget?: number;
  is_paid?: boolean;
  posted_at: string;
  deadline?: string;
  tags?: string[];
  match_score?: number;
  reason_for_recommendation?: string;
  matching_factors?: string[];
  view_count?: number;
  applicant_count?: number;
  created_at: string;
  end_date?: string;
  creator_profile?: any;
}


interface OpportunityRecommendationsProps {
  recommendations: Recommendation[];
  userType: 'creator' | 'model' | 'hirer';
}

const OpportunityRecommendations: React.FC<OpportunityRecommendationsProps> = ({ 
  recommendations, 
  userType 
}) => {

    const [nearByrecommendations, setNearByrecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const response = await dashboardAPI.getPersonalizedRecommendations();
      // console.log("response", response)
      setNearByrecommendations(response.data.data.data == null ? [] : response.data.data.data);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    } finally {
      setLoading(false);
    }
  };
      const { user } = useAppSelector((state) => state.auth);
    
  const getRecommendationIcon = (type: string) => {
    switch (type) {
      case 'opportunity':
        return userType === 'creator' ? SparklesIcon : CameraIcon;
      case 'collaboration':
        return HeartIcon;
      case 'user':
        return userType === 'creator' ? SparklesIcon : CameraIcon;
      default:
        return EyeIcon;
    }
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 90) return 'text-emerald-700 bg-emerald-50 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800';
    if (score >= 70) return 'text-teal-700 bg-teal-50 border-teal-100 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-800';
    return 'text-blue-700 bg-blue-50 border-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
  };

  const formatBudget = (budget: number) => {
    if (budget >= 100000) {
      return `₹${(budget / 100000).toFixed(1)}L`;
    } else if (budget >= 1000) {
      return `₹${(budget / 1000).toFixed(0)}K`;
    }
    return `₹${budget}`;
  };

  return (
    <div className="bg-white max-h-[840px] overflow-y-auto rounded-2xl p-4 md:p-6 shadow-sm border border-gray-200 dark:bg-gray-900 dark:border-gray-800">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">
          Recommended Opportunities
        </h2>
        <Link 
          to="/opportunities"
          className="text-sm text-teal-600 hover:text-teal-700 font-medium dark:text-teal-400 dark:hover:text-teal-300"
        >
          Explore More →
        </Link>
      </div>

      <div className="flex flex-col gap-4">
        {nearByrecommendations.length > 0 ? (
          nearByrecommendations.map((rec, index) => {
            const Icon = getRecommendationIcon('opportunity');
            const matchScoreColor = getMatchScoreColor(rec.match_score || 75);

            return (
              <motion.div
                key={rec.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => {
                  // Navigate to opportunity details
                  if (!rec.creator_profile) {
                     // If it's a user recommendation (no creator_profile), navigate to profile
                     // But we are now showing opportunities, so this case might be rare for creators
                     // If it IS a user recommendation, rec.id is the user ID.
                     // But wait, rec.type 'user' -> rec.id is user ID.
                     // rec.type 'opportunity' -> rec.id is opp ID.
                     if (rec.type === 'user') {
                        window.location.href = `/profile/${rec.id}`;
                     } else {
                        window.location.href = `/opportunities/${rec.id}`;
                     }
                  } else {
                     window.location.href = `/opportunities/${rec.id}`;
                  }
                }}
                className="p-4 bg-white border border-gray-100 rounded-xl hover:shadow-md hover:border-teal-100 transition-all duration-200 group cursor-pointer dark:bg-gray-800 dark:border-gray-700 dark:hover:border-teal-900" 
              >
                {/* Header: Creator & Match Score */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                         <Link 
                            to={`/profile/${rec.creator_profile?.id || rec.creator?.id}`}
                            className="block relative z-10"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Avatar
                                src={rec.creator_profile?.profile_pic_signed || rec.creator_profile?.profile_pic || rec.creator?.avatar}
                                alt={rec.creator_profile?.first_name || rec.creator?.name || 'User'}
                                size="md"
                                className="ring-2 ring-gray-50 group-hover:ring-teal-50 transition-all dark:ring-gray-700 dark:group-hover:ring-teal-900"
                            />
                        </Link>
                        <div>
                            <Link 
                                to={`/profile/${rec.creator_profile?.id || rec.creator?.id}`}
                                className="font-bold text-gray-900 hover:text-teal-600 transition-colors text-sm block relative z-10 dark:text-white dark:hover:text-teal-400"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {rec.creator_profile ? `${rec.creator_profile.first_name} ${rec.creator_profile.last_name}` : rec.creator?.name}
                            </Link>
                            
                            <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                                {(rec.creator_profile?.rating > 0 || (rec.creator?.rating !== undefined && rec.creator.rating > 0)) ? (
                                    <div className="flex items-center space-x-1">
                                        <div className="flex items-center space-x-1 bg-yellow-50 px-1.5 py-0.5 rounded text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                                            <StarIconSolid className="h-3 w-3 text-yellow-500" />
                                            <span className="font-medium">
                                                {(rec.creator_profile?.rating || rec.creator?.rating || 0).toFixed(1)}
                                            </span>
                                        </div>
                                        <span className="text-gray-400 dark:text-gray-500">
                                            ({rec.creator_profile?.review_count || 0} reviews)
                                        </span>
                                    </div>
                                ) : (
                                    <span className="text-gray-400 italic dark:text-gray-500">No rating yet</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {rec.match_score && (
                        <div className={`flex flex-col items-end`}>
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold border ${matchScoreColor}`}>
                                <SparklesIcon className="h-3 w-3 mr-1" />
                                {rec.match_score}% Match
                            </div>
                        </div>
                    )}
                </div>

                {/* Main Content */}
                <div className="mb-3 pl-[52px]">
                    {/* Only show title if it's NOT a user recommendation (or if title is different from name) */}
                    {!rec.creator_profile && (
                        <div 
                            className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors line-clamp-1 mb-1 block dark:text-white dark:group-hover:text-teal-400"
                        >
                            {rec.title}
                        </div>
                    )}
                    {rec.creator_profile && (
                         <div 
                            className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors line-clamp-1 mb-1 block dark:text-white dark:group-hover:text-teal-400"
                        >
                            {rec.title}
                        </div>
                    )}
                    
                    {/* Matching Factors */}
                    {rec.matching_factors && rec.matching_factors.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {rec.matching_factors.slice(0, 2).map((factor, i) => (
                                <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-50 text-teal-700 border border-teal-100 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800">
                                    <CheckCircleIcon className="h-3 w-3 mr-1 text-teal-500" />
                                    {factor}
                                </span>
                            ))}
                        </div>
                    )}

                    <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed dark:text-gray-400">
                        {rec.description}
                    </p>
                </div>

                {/* Footer Details */}
                <div className="pl-[52px] pt-3 border-t border-gray-50 flex items-center justify-between text-xs dark:border-gray-700">
                    <div className="flex items-center space-x-3 text-gray-500 min-w-0 flex-1 mr-2 dark:text-gray-400">
                        {/* Location / Distance */}
                        <div className="flex items-center space-x-1 text-gray-700 font-medium min-w-0 dark:text-gray-300">
                            <MapPinIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 dark:text-gray-500" />
                            <span className="truncate">
                                {rec.distance_km 
                                    ? `${rec.distance_km.toFixed(1)} km away` 
                                    : (rec.location || 'Remote')
                                }
                            </span>
                        </div>

                        {/* Budget */}
                        {rec.budget !== undefined && (
                            <div className="flex items-center space-x-1 flex-shrink-0">
                                <CurrencyDollarIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                                <span>{rec.is_paid ? formatBudget(rec.budget) : 'TFP'}</span>
                            </div>
                        )}
                    </div>

                    <div className="text-gray-400 whitespace-nowrap flex-shrink-0 dark:text-gray-500">
                        {formatDistanceToNow(new Date(rec.created_at), { addSuffix: true })
                            .replace('about ', '')
                            .replace('less than a minute', 'just now')
                            .replace(/ minutes?/, 'm')
                            .replace(/ hours?/, 'h')
                            .replace(/ days?/, 'd')
                            .replace(/ months?/, 'mo')
                            .replace(/ years?/, 'y')}
                    </div>
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200 dark:bg-gray-800 dark:border-gray-700">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm dark:bg-gray-700">
              {userType === 'creator' ? (
                <SparklesIcon className="h-6 w-6 text-teal-500" />
              ) : (
                <CameraIcon className="h-6 w-6 text-teal-500" />
              )}
            </div>
            <p className="text-gray-900 font-medium text-sm dark:text-white">No recommendations yet</p>
            <p className="text-gray-500 text-xs mt-1 max-w-xs mx-auto dark:text-gray-400">
              We're analyzing your profile to find the best matches.
            </p>
            {!user?.is_profile_complete && (
                <Link 
                to="/profile/edit"
                className="inline-block mt-3 text-xs text-teal-600 hover:text-teal-700 font-medium bg-teal-50 px-3 py-1.5 rounded-full dark:bg-teal-900/30 dark:text-teal-300"
                >
                Complete Profile →
                </Link>
            )}
          </div>
        )}
      </div>

      {/* Recommendation Settings */}
      {nearByrecommendations.length > 0 && (
        <div className="mt-4 flex items-center justify-between px-2">
            <div className="flex items-center space-x-1 text-[10px] text-gray-400 dark:text-gray-500">
                <SparklesIcon className="h-3 w-3" />
                <span>AI-powered recommendations</span>
            </div>
            <button className="text-xs text-gray-500 hover:text-teal-600 font-medium transition-colors dark:text-gray-400 dark:hover:text-teal-400">
              Adjust Preferences
            </button>
        </div>
      )}
    </div>
  );
};

export default OpportunityRecommendations;
